import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteInput = ({ leadId, onQuoteSubmit }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    age: "",
    gender: "",
    smoking_status: "",
    state: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleAgeChange = (value) => {
    setFormData((prev) => ({ ...prev, age: value }));
    if (errors.age) {
      setErrors((prev) => ({ ...prev, age: "" }));
    }
  };

  const handleGenderChange = (value) => {
    setFormData((prev) => ({ ...prev, gender: value }));
    if (errors.gender) {
      setErrors((prev) => ({ ...prev, gender: "" }));
    }
  };

  const handleSmokingChange = (value) => {
    setFormData((prev) => ({ ...prev, smoking_status: value }));
    if (errors.smoking_status) {
      setErrors((prev) => ({ ...prev, smoking_status: "" }));
    }
  };

  const handleStateChange = (value) => {
    setFormData((prev) => ({ ...prev, state: value }));
    if (errors.state) {
      setErrors((prev) => ({ ...prev, state: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const newErrors = {};
    if (!formData.age) {
      newErrors.age = "Please select your age";
    }
    if (!formData.gender) {
      newErrors.gender = "Please select your gender";
    }
    if (!formData.smoking_status) {
      newErrors.smoking_status = "Please select your smoking status";
    }
    if (!formData.state) {
      newErrors.state = "Please select your state of residency";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/quotes`, {
        lead_id: leadId,
        age: parseInt(formData.age),
        gender: formData.gender,
        state: formData.state,
        smoking_status: formData.smoking_status,
      });
      toast.success("Quote calculated successfully!");
      onQuoteSubmit(response.data);
      navigate("/results");
    } catch (error) {
      console.error("Error calculating quote:", error);
      toast.error(error.response?.data?.detail || "Failed to calculate quote. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const usStates = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
    "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
    "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
    "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
    "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
    "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
    "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
  ];

  const ageRanges = [
    { value: "25", label: "18-29 years" },
    { value: "35", label: "30-39 years" },
    { value: "45", label: "40-49 years" },
    { value: "55", label: "50-59 years" },
    { value: "65", label: "60-69 years" },
    { value: "75", label: "70-79 years" },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom right, #F5F5F5, #FFFFFF, #FAFAFA)' }}>
      {/* Header */}
      <header className="border-b border-gray-200 bg-white/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/financial-flight-logo.png" 
              alt="Financial Flight" 
              className="w-10 h-10"
            />
            <span className="text-xl font-bold" style={{ color: '#2E2E2E' }}>Financial Flight</span>
          </div>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 transition-opacity"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </header>

      {/* Main Content */}
      <section className="max-w-3xl mx-auto px-6 py-16">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-2xl">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full text-white flex items-center justify-center text-sm font-semibold" style={{ backgroundColor: '#D4AF37' }}>
                1
              </div>
              <span className="text-sm text-gray-500">Contact Info</span>
            </div>
            <div className="w-12 h-0.5" style={{ backgroundColor: '#D4AF37' }}></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full text-white flex items-center justify-center text-sm font-semibold" style={{ backgroundColor: '#D4AF37' }}>
                2
              </div>
              <span className="text-sm font-medium" style={{ color: '#2E2E2E' }}>Demographics</span>
            </div>
            <div className="w-12 h-0.5 bg-gray-200"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-sm font-semibold">
                3
              </div>
              <span className="text-sm text-gray-500">Your Quote</span>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#2E2E2E' }}>Tell Us About Yourself</h2>
            <p className="text-sm text-gray-600">
              We need a few details to calculate your personalized insurance estimate
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8" data-testid="quote-input-form">
            {/* Age Selection */}
            <div className="space-y-3">
              <Label className="text-base font-semibold" style={{ color: '#2E2E2E' }}>Age Range *</Label>
              <Select value={formData.age} onValueChange={handleAgeChange}>
                <SelectTrigger className="h-12" data-testid="age-select">
                  <SelectValue placeholder="Select your age range" />
                </SelectTrigger>
                <SelectContent>
                  {ageRanges.map((range) => (
                    <SelectItem key={range.value} value={range.value} data-testid={`age-option-${range.value}`}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.age && (
                <p className="text-sm text-red-600" data-testid="age-error">{errors.age}</p>
              )}
            </div>

            {/* Gender Selection */}
            <div className="space-y-3">
              <Label className="text-base font-semibold" style={{ color: '#2E2E2E' }}>Gender *</Label>
              <RadioGroup value={formData.gender} onValueChange={handleGenderChange}>
                <div className="grid grid-cols-3 gap-3">
                  <label
                    htmlFor="male"
                    className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.gender === "male"
                        ? "border-2"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    style={formData.gender === "male" ? { borderColor: '#D4AF37', backgroundColor: '#FFF9E6' } : {}}
                  >
                    <RadioGroupItem value="male" id="male" data-testid="gender-male" />
                    <span className="text-sm font-medium">Male</span>
                  </label>
                  <label
                    htmlFor="female"
                    className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.gender === "female"
                        ? "border-2"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    style={formData.gender === "female" ? { borderColor: '#D4AF37', backgroundColor: '#FFF9E6' } : {}}
                  >
                    <RadioGroupItem value="female" id="female" data-testid="gender-female" />
                    <span className="text-sm font-medium">Female</span>
                  </label>
                  <label
                    htmlFor="other"
                    className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.gender === "other"
                        ? "border-2"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    style={formData.gender === "other" ? { borderColor: '#D4AF37', backgroundColor: '#FFF9E6' } : {}}
                  >
                    <RadioGroupItem value="other" id="other" data-testid="gender-other" />
                    <span className="text-sm font-medium">Other</span>
                  </label>
                </div>
              </RadioGroup>
              {errors.gender && (
                <p className="text-sm text-red-600" data-testid="gender-error">{errors.gender}</p>
              )}
            </div>

            {/* Smoking Status */}
            <div className="space-y-3">
              <Label className="text-base font-semibold" style={{ color: '#2E2E2E' }}>Smoking Status *</Label>
              <RadioGroup value={formData.smoking_status} onValueChange={handleSmokingChange}>
                <div className="grid grid-cols-2 gap-3">
                  <label
                    htmlFor="non_smoker"
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.smoking_status === "non_smoker"
                        ? "border-2"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    style={formData.smoking_status === "non_smoker" ? { borderColor: '#D4AF37', backgroundColor: '#FFF9E6' } : {}}
                  >
                    <RadioGroupItem value="non_smoker" id="non_smoker" data-testid="smoking-non-smoker" />
                    <div>
                      <div className="text-sm font-medium">Non-Smoker</div>
                      <div className="text-xs text-gray-500">Lower rates available</div>
                    </div>
                  </label>
                  <label
                    htmlFor="smoker"
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.smoking_status === "smoker"
                        ? "border-2"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    style={formData.smoking_status === "smoker" ? { borderColor: '#D4AF37', backgroundColor: '#FFF9E6' } : {}}
                  >
                    <RadioGroupItem value="smoker" id="smoker" data-testid="smoking-smoker" />
                    <div>
                      <div className="text-sm font-medium">Smoker</div>
                      <div className="text-xs text-gray-500">Accurate estimates</div>
                    </div>
                  </label>
                </div>
              </RadioGroup>
              {errors.smoking_status && (
                <p className="text-sm text-red-600" data-testid="smoking-error">{errors.smoking_status}</p>
              )}
            </div>

            {/* State Selection */}
            <div className="space-y-3">
              <Label className="text-base font-semibold" style={{ color: '#2E2E2E' }}>State of Residency *</Label>
              <Select value={formData.state} onValueChange={handleStateChange}>
                <SelectTrigger className="h-12" data-testid="state-select">
                  <SelectValue placeholder="Select your state" />
                </SelectTrigger>
                <SelectContent>
                  {usStates.map((state) => (
                    <SelectItem key={state} value={state} data-testid={`state-option-${state.toLowerCase().replace(/\s+/g, '-')}`}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.state && (
                <p className="text-sm text-red-600" data-testid="state-error">{errors.state}</p>
              )}
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base font-semibold transition-all hover:opacity-90"
                style={{ backgroundColor: '#D4AF37', color: '#2E2E2E' }}
                data-testid="calculate-quote-button"
              >
                {loading ? "Calculating..." : "Calculate My Quote"}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default QuoteInput;